import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import WarningIcon from '@material-ui/icons/Warning';
import styled from 'styled-components';
import { Button } from '@material-ui/core';
import { InputBaseContainer, InputSelectWithLabel } from '../../../../../molecules';
import { labels } from '../constants';
import { RootState } from '../../../../../redux/root';
import { dispatch } from '../../../../../redux/store';
import { setInspectionsTypeData, setSelectedInspection } from '../../../../../redux/slices/inspectionsSlice';
import { FORM_LIST_LIMIT, useGetInspectionFormsQuery } from '../../../../../redux/rtkQuery/apiFormbuilderSlice';
import { clearTemplateTexts } from '../../../../../organisms/ThresholdReports/docOvelayTool';
import { setPrevType, setSelectedForm } from '../../../../../redux/slices/thresholdFormSlice';
import { needToShowInspectionTypePopup } from '../utils';
import { getCurrentUser } from '../../../../../api/userSettings';
import { getUsers } from '../../../../../api/users';
import { GET_ALL_QUERY_PARAMS } from '../../../../../constants';
import { USER_TYPES_VALUES } from '../../../../../constants/forms';

const name = 'inspectionType';
const InspectionType = ({ control, errors, setValue, getValues, setDisabledSubmit, watch }) => {
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const { data: forms } = useGetInspectionFormsQuery({ limit: FORM_LIST_LIMIT });
  const prev = useSelector((s: RootState) => s.thresholdForms.prevType);
  const { inspectionsTypeData, selectedInspection } = useSelector((s: RootState) => s.inspections);
  const inspectionType = watch(name);

  // EA-1081: In the Inspector Module the dropdown must only offer inspection
  // types the logged-in inspector is qualified for (by trade). `inspectionsTypeData`
  // is a shared Redux key repopulated by several loaders, so we filter at the
  // point of consumption rather than mutating the store. `null` = "not an
  // inspector / trades unknown" → show everything.
  const [inspectorTrades, setInspectorTrades] = useState<number[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meResponse: any = await getCurrentUser(undefined);
        const me = meResponse?.data;
        const isInspector = Number(me?.userRole) === Number(USER_TYPES_VALUES.INSPECTOR);
        if (!isInspector || !me?.id) {
          if (!cancelled) setInspectorTrades(null);
          return;
        }
        const usersResponse: any = await getUsers({
          ...GET_ALL_QUERY_PARAMS,
          userRole: USER_TYPES_VALUES.INSPECTOR,
        });
        const meWithTrades = (usersResponse?.data?.results || []).find((u: any) => u.id === me.id);
        const trades: number[] = (meWithTrades?.trade || meWithTrades?.trades || []).map((t: any) =>
          Number(typeof t === 'object' && t !== null ? t.id : t),
        );
        if (!cancelled) setInspectorTrades(trades.length ? trades : null);
      } catch (e) {
        if (!cancelled) setInspectorTrades(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Options actually shown in the dropdown. Non-inspectors (trades === null)
  // see the full list; inspectors see only trade-matched types. The currently
  // selected type is always kept so an existing value never disappears.
  const filteredOptions = React.useMemo(() => {
    const all = inspectionsTypeData || [];
    if (!inspectorTrades || !inspectorTrades.length) return all;
    const selectedId = inspectionType?.id ?? inspectionType;
    return all.filter((type: any) => {
      if (selectedId && (type.id === selectedId || type.id === Number(selectedId))) return true;
      const typeTrades = (type.tradeIds || type.trades || type.trade_ids || []).map((t: any) =>
        Number(typeof t === 'object' && t !== null ? t.id : t),
      );
      return typeTrades.some((id: number) => inspectorTrades.includes(id));
    });
  }, [inspectionsTypeData, inspectorTrades, inspectionType]);
  useEffect(() => {
    dispatch(setInspectionsTypeData([]));
    return () => {
      dispatch(setInspectionsTypeData(undefined));
      dispatch(setPrevType(undefined));
    };
  }, []);

  useEffect(() => {
    if (!prev) {
      dispatch(setPrevType(inspectionType));
      return;
    }
    if (!inspectionType) {
      return;
    }
    loadType();
    const existingForm =
      forms && forms.results ? forms.results.find(frm => frm.inspectionTypes.indexOf(inspectionType.id || inspectionType) > -1) : undefined;
    const existingJuri =
      forms && forms.results
        ? forms.results.find(frm => frm.jurisdictions.indexOf(Number(selectedInspection.jurisdictionId)) > -1)
        : undefined;
    // Compare the type's IDENTITY, not its `.name`.
    //
    // `prev` starts as the raw inspection-type id out of the form's default
    // values and only becomes the option OBJECT once inspectionsTypeData loads
    // (see the effect below). Across those two shapes `inspectionType.name !==
    // prev.name` reads 'Final' !== undefined — a change on an inspection nobody
    // touched. That dropped an untouched inspection into the branch below, and
    // since needToShowInspectionTypePopup correctly answers "no popup, the user
    // has not selected anything yet", it landed on resetFormInStore(): both
    // `selectedForm` and `formMarkup` wiped, no form id left to fetch page
    // images with, and Inspection Notes stuck on "Loading report…".
    const prevId = prev?.id ?? prev;
    const currentId = inspectionType?.id ?? inspectionType;
    if (String(prevId) !== String(currentId)) {
      if (forms && needToShowInspectionTypePopup(forms, inspectionType, selectedInspection, prev)) {
        setShowPopup(true);
        // handling the case if these is like st.pete (jurisdiction with manual form)
        if (existingJuri) {
          assignForm(existingJuri);
        } else if (!existingForm) {
          resetFormInStore();
        }
      } else if (existingForm) {
          assignForm(existingForm);
        } else if (existingJuri) {
          // A form attached to a JURISDICTION rather than an inspection type
          // (St Pete, City of Miami). `existingForm` only ever searches
          // `frm.inspectionTypes`, so it is always undefined for these — without
          // this branch every such form fell through to the reset below and was
          // thrown away, even though InspectionTabs had just matched it.
          assignForm(existingJuri);
        } else {
          resetFormInStore();
        }
    } else if (existingForm) {
      assignForm(existingForm);
    }
  }, [inspectionType]);
  useEffect(() => {
    const initValue = loadType();
    if (initValue && !isNaN(prev)) {
      dispatch(setPrevType(initValue));
    }
  }, [inspectionsTypeData, selectedInspection]);

  const assignForm = theForm => {
    const dataTemplate = clearTemplateTexts(theForm.formMarkup, selectedInspection);
    dispatch(setSelectedInspection({ ...selectedInspection, activityTypeName: inspectionType.name, formMarkup: dataTemplate }));
    dispatch(setSelectedForm(theForm));
  };
  const resetFormInStore = () => {
    // this is the case that we selected inspectionType that has no fillable report
    dispatch(setSelectedInspection({ ...selectedInspection, activityTypeName: inspectionType.name, formMarkup: undefined }));
    dispatch(setSelectedForm(undefined));
  };
  const loadType = () => {
    let currentValue = getValues(name) || selectedInspection.inspectionType;
    if (!currentValue) {
      return;
    }

    if (!isNaN(currentValue)) {
      currentValue = selectedInspection.activityTypeName;
    }
    // this is the case where we changed the InspectionType before we persist it
    if (currentValue && typeof currentValue !== 'string' && currentValue.id && currentValue.id !== selectedInspection.inspectionType) {
      return;
    }
    if (selectedInspection.inspectionType && inspectionsTypeData?.length > 0) {
      const value = inspectionsTypeData.find(
        item =>
          item.id === selectedInspection.inspectionType ||
          // @ts-ignore
          item.name === selectedInspection.inspectionType,
      );
      // A miss must NOT blank the field.
      //
      // `setValue(name, undefined)` used to run unconditionally, so whenever the
      // lookup failed it actively erased a populated Inspection Type — and
      // `Pdf.tsx`'s isDisabled() reads that empty field as "not ready" and greys
      // out Create Report until a full page refresh. The lookup misses when
      // `inspectionsTypeData` still holds the list from the inspection the user
      // came from, which is exactly what happens switching between inspections
      // without a reload.
      if (!value) {
        setDisabledSubmit(true);
        return undefined;
      }
      setValue(name, value);
      setDisabledSubmit(false);
      return value;
    }
      setDisabledSubmit(true);
  };

  const onCancel = () => {
    setValue(name, prev);
    const prevForm = forms.results.find(frm => frm.inspectionTypes.indexOf(prev.id) > -1);
    if (prevForm) {
      assignForm(prevForm);
    }
    setShowPopup(false);
  };

  const onOK = () => {
    const newForm = forms.results.find(frm => frm.inspectionTypes.indexOf(inspectionType.id) > -1);
    if (newForm && newForm.formMarkup) {
      const dataTemplate = clearTemplateTexts(newForm.formMarkup, { ...selectedInspection, formMarkup: undefined });
      dispatch(setSelectedInspection({ ...selectedInspection, activityTypeName: inspectionType.name, formMarkup: dataTemplate }));
      dispatch(setSelectedForm(newForm));
    }
    dispatch(setPrevType(inspectionType));
    setShowPopup(false);
  };
  return (
    <Wrapper>
      <InputBaseContainer>
        <Controller
          name={name}
          defaultValue=""
          control={control}
          // A string message (not `required: true`) — InputSelectWithLabel renders
          // error.message, so a boolean rule shows an empty error.
          rules={{ required: `${labels.inspectionType} is required` }}
          render={props => {
            return (
              <InputSelectWithLabel
                showInRow
                placeholder=""
                menuPlacement="bottom"
                options={filteredOptions}
                isLoading={!inspectionsTypeData || inspectionsTypeData?.length === 0}
                isRequired
                label={labels.inspectionType}
                error={errors[name]}
                getOptionValue={(item: any) => item.id || Math.random()}
                getOptionLabel={(item: any) => item.name}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            );
          }}
        />
      </InputBaseContainer>
      {showPopup && (
        <Popup>
          <Header>Confirmation</Header>
          <Message>
            <WarningIcon />
            <p>Changing "Inspection-Type" Will Reset All the Inspection-Notes.</p>
            <p>Do you want to continue?</p>
          </Message>
          <Buttons>
            <Button color="primary" variant="contained" onClick={onCancel}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={onOK}>
              OK
            </Button>
          </Buttons>
        </Popup>
      )}
    </Wrapper>
  );
};

export default InspectionType;

const Wrapper = styled.div`
  position: relative;
`;

const Header = styled.div`
  width: 100%;
  text-align: center;
  font-size: 25px;
`;

const Message = styled.div`
  position: relative;
  text-align: center;
  svg {
    position: absolute;
    color: red;
    right: 5px;
  }
`;

const Buttons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  margin: 20px;
  margin-top: 40px;
  button {
    width: 150px;
  }
`;

const Popup = styled.div`
  position: absolute;
  width: 600px;
  background-color: #fff;
  border: 1px solid grey;
  padding: 10px;
  z-index: 2;
  left: -268px;
  top: -32px;
  box-shadow: 10px 10px rgba(200, 200, 200, 0.8);
  border-radius: 8px;
`;
